import {addMedicalOrderFollowup, getMedicalOrderFollowups, updateMedicalOrderFollowup} from "@/api";
import {ElMessage, ElMessageBox} from "element-plus";

// 保留服务端字典编码，下拉与列表统一展示当前文案，不改写历史数据快照。
const TREATMENT_STATUS_LABELS = {
    "1": "病好了不需要继续治疗了",
    "2": "药没吃完继续吃药",
    "3": "没效果不再复诊了",
    "4": "确定复诊"
};

const EMPTY_DRAFT = () => ({
    followupContent: "",
    treatmentStatusValue: ""
});

export default {
    name: "MedicalOrderFollowup",
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        orderId: {
            type: [String, Number],
            default: null
        }
    },
    emits: ["update:modelValue"],
    data() {
        return {
            loading: false,
            loadError: "",
            loadRequestId: 0,
            saving: false,
            summary: null,
            followups: [],
            followupSortOrder: "desc",
            statuses: [],
            isAdding: false,
            newDraft: EMPTY_DRAFT(),
            editingFollowupId: null,
            editDraft: EMPTY_DRAFT(),
            editInitial: EMPTY_DRAFT()
        };
    },
    computed: {
        // 仅调整展示顺序，新增和编辑保存后继续沿用当前排序，不修改原始记录。
        sortedFollowups() {
            const direction = this.followupSortOrder === "asc" ? 1 : -1;
            return [...this.followups].sort((left, right) => direction * this.compareFollowups(left, right));
        }
    },
    watch: {
        modelValue(value) {
            if (value) {
                this.loadData();
            }
        },
        orderId(value, oldValue) {
            if (this.modelValue && value !== oldValue) {
                this.resetEditor();
                this.loadData();
            }
        }
    },
    methods: {
        // 按诊疗语义复用主题色，未知历史状态使用中性灰色。
        treatmentStatusType(record) {
            const types = {
                "1": "success",
                "2": "warning",
                "3": "danger",
                "4": "primary"
            };
            return types[String(record.treatmentStatusValue)] || "info";
        },

        unwrapResponse(response) {
            if (response && response.data && typeof response.data.code !== "undefined") {
                return response.data;
            }
            return response || {};
        },

        async refreshFollowups() {
            if (this.loading || this.saving) {
                return;
            }
            if (this.hasUnsavedChanges() && !(await this.confirmDiscardIfNeeded())) {
                return;
            }
            this.resetEditor();
            await this.loadData();
        },

        async loadData() {
            if (this.orderId === null || this.orderId === undefined || String(this.orderId).trim() === "" || String(this.orderId) === "0") {
                this.loadError = "缺少药方标识，无法加载回访记录";
                this.loading = false;
                return;
            }
            const requestId = ++this.loadRequestId;
            const requestOrderId = this.orderId;
            this.summary = null;
            this.followups = [];
            this.statuses = [];
            this.loading = true;
            this.loadError = "";
            try {
                const response = await getMedicalOrderFollowups(requestOrderId);
                if (requestId !== this.loadRequestId || requestOrderId !== this.orderId) {
                    return;
                }
                const result = this.unwrapResponse(response);
                if (result.code !== 200) {
                    throw new Error(result.msg || "回访记录加载失败");
                }
                const data = result.data || {};
                if (!data.medicalOrder || !data.medicalOrder.serialNo || !Array.isArray(data.followups) || !Array.isArray(data.statuses)) {
                    throw new Error("药方基础信息或回访记录不完整，请重试");
                }
                this.summary = data.medicalOrder;
                this.followups = data.followups
                    .map(record => this.normalizeRecord(record))
                    .sort(this.compareFollowups);
                this.statuses = data.statuses.map(status => ({
                    ...status,
                    dictLabel: TREATMENT_STATUS_LABELS[String(status.dictValue)] || status.dictLabel
                }));
            } catch (error) {
                if (requestId !== this.loadRequestId) {
                    return;
                }
                this.loadError = error.message || "回访记录加载失败，请重试";
                ElMessage.error(this.loadError);
            } finally {
                if (requestId === this.loadRequestId) {
                    this.loading = false;
                }
            }
        },

        normalizeRecord(record) {
            return {
                ...record,
                followupId: record.followupId !== null && record.followupId !== undefined ? record.followupId : record.id,
                orderId: record.orderId !== null && record.orderId !== undefined ? record.orderId : this.orderId,
                followupContent: record.followupContent || record.content || "",
                treatmentStatusValue: record.treatmentStatusValue || record.statusValue || "",
                treatmentStatusLabel: record.treatmentStatusLabel || record.statusLabel || ""
            };
        },

        compareFollowups(left, right) {
            const leftTime = String(left.createTime || "");
            const rightTime = String(right.createTime || "");
            const timeCompare = leftTime.localeCompare(rightTime);
            if (timeCompare !== 0) {
                return timeCompare;
            }
            return Number(left.followupId || 0) - Number(right.followupId || 0);
        },

        summaryValue(keys) {
            if (!this.summary) {
                return "—";
            }
            const keyList = Array.isArray(keys) ? keys : [keys];
            for (const key of keyList) {
                const value = this.summary[key];
                if (value !== null && value !== undefined && String(value).trim() !== "") {
                    return value;
                }
            }
            return "—";
        },

        statusLabel(record) {
            // 列表与下拉统一使用当前诊疗文案，未知编码仍兼容历史快照。
            const currentLabel = TREATMENT_STATUS_LABELS[String(record.treatmentStatusValue)];
            if (currentLabel) {
                return currentLabel;
            }
            if (record.treatmentStatusLabel) {
                return record.treatmentStatusLabel;
            }
            const item = this.statuses.find(status => String(status.dictValue) === String(record.treatmentStatusValue));
            return item ? item.dictLabel : (record.treatmentStatusValue || "—");
        },

        displayName(record, type) {
            const names = type === "create"
                ? ["createName", "createBy", "createUserName"]
                : ["updateName", "updateBy", "updateUserName"];
            for (const name of names) {
                if (record && record[name] !== null && record[name] !== undefined && String(record[name]).trim() !== "") {
                    return record[name];
                }
            }
            return "—";
        },

        todayDateOnly() {
            const now = new Date();
            const pad = value => String(value).padStart(2, "0");
            return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        },

        formatDateTime(value) {
            if (!value) {
                return "—";
            }
            return String(value).replace("T", " ").slice(0, 19);
        },

        hasDraftValue(draft) {
            return Boolean(draft && (String(draft.followupContent || "").trim() || draft.treatmentStatusValue));
        },

        hasUnsavedChanges() {
            if (this.isAdding) {
                return this.hasDraftValue(this.newDraft);
            }
            if (this.editingFollowupId !== null) {
                return this.isDraftChanged(this.editDraft, this.editInitial);
            }
            return false;
        },

        isDraftChanged(current, initial) {
            return String(current.followupContent || "") !== String(initial.followupContent || "")
                || String(current.treatmentStatusValue || "") !== String(initial.treatmentStatusValue || "");
        },

        validateDraft(draft) {
            if (!String(draft.followupContent || "").trim()) {
                ElMessage.warning("请填写回访记录内容");
                return false;
            }
            if (!draft.treatmentStatusValue) {
                ElMessage.warning("请选择诊疗情况");
                return false;
            }
            return true;
        },

        openAdd() {
            if (this.isAdding || this.editingFollowupId !== null || this.saving) {
                return;
            }
            this.isAdding = true;
            this.newDraft = EMPTY_DRAFT();
        },

        async startEdit(record) {
            if (this.isAdding || this.editingFollowupId !== null || this.saving) {
                return;
            }
            if (record && record.treatmentStatusValue && !this.statuses.some(status => String(status.dictValue) === String(record.treatmentStatusValue))) {
                const label = this.statusLabel(record);
                if (!label || label === record.treatmentStatusValue) {
                    ElMessage.warning("原诊疗情况已停用且无可识别文案，暂不能编辑");
                    return;
                }
                this.statuses = [...this.statuses, {dictValue: record.treatmentStatusValue, dictLabel: label, retired: true}];
            }
            this.editingFollowupId = record.followupId;
            this.editDraft = {
                followupContent: record.followupContent || "",
                treatmentStatusValue: record.treatmentStatusValue || ""
            };
            this.editInitial = {...this.editDraft};
        },

        async cancelAdd() {
            if (this.saving) {
                return;
            }
            if (await this.confirmDiscardIfNeeded()) {
                this.isAdding = false;
                this.newDraft = EMPTY_DRAFT();
            }
        },

        async cancelEdit() {
            if (this.saving) {
                return;
            }
            if (await this.confirmDiscardIfNeeded()) {
                this.editingFollowupId = null;
                this.editDraft = EMPTY_DRAFT();
                this.editInitial = EMPTY_DRAFT();
            }
        },

        async confirmDiscardIfNeeded() {
            if (!this.hasUnsavedChanges()) {
                return true;
            }
            try {
                await ElMessageBox.confirm("当前填写内容尚未保存，确定放弃吗？", "提示", {
                    confirmButtonText: "放弃并退出",
                    cancelButtonText: "继续填写",
                    type: "warning"
                });
                return true;
            } catch (error) {
                return false;
            }
        },

        async saveNew() {
            if (this.saving || !this.validateDraft(this.newDraft)) {
                return;
            }
            this.saving = true;
            try {
                const response = await addMedicalOrderFollowup({
                    orderId: this.orderId,
                    followupContent: this.newDraft.followupContent,
                    treatmentStatusValue: this.newDraft.treatmentStatusValue
                });
                const result = this.unwrapResponse(response);
                if (result.code !== 200) {
                    throw new Error(result.msg || "回访记录保存失败");
                }
                const savedRecord = this.normalizeRecord(result.data || {});
                if (!savedRecord.followupId || String(savedRecord.orderId) !== String(this.orderId)
                    || !savedRecord.createTime || !savedRecord.createName || !savedRecord.treatmentStatusLabel) {
                    throw new Error("保存结果缺少服务端回访信息，请重试");
                }
                this.followups = [...this.followups, savedRecord].sort(this.compareFollowups);
                this.isAdding = false;
                this.newDraft = EMPTY_DRAFT();
                ElMessage.success("回访记录保存成功");
            } catch (error) {
                ElMessage.error(error.message || "回访记录保存失败，请重试");
            } finally {
                this.saving = false;
            }
        },

        async saveEdit(record) {
            if (this.saving || !this.validateDraft(this.editDraft)) {
                return;
            }
            this.saving = true;
            try {
                const response = await updateMedicalOrderFollowup(this.orderId, record.followupId, {
                    followupContent: this.editDraft.followupContent,
                    treatmentStatusValue: this.editDraft.treatmentStatusValue,
                    version: record.version
                });
                const result = this.unwrapResponse(response);
                if (result.code !== 200) {
                    throw new Error(result.msg || "回访记录保存失败");
                }
                const updatedRecord = this.normalizeRecord(result.data || {});
                if (String(updatedRecord.followupId) !== String(record.followupId)
                    || String(updatedRecord.orderId) !== String(this.orderId)
                    || String(updatedRecord.createTime) !== String(record.createTime)
                    || Number(updatedRecord.version) <= Number(record.version)) {
                    throw new Error("保存结果不完整或版本冲突，请重新加载后重试");
                }
                this.followups = this.followups.map(item => String(item.followupId) === String(record.followupId)
                    ? updatedRecord
                    : item).sort(this.compareFollowups);
                this.editingFollowupId = null;
                this.editDraft = EMPTY_DRAFT();
                this.editInitial = EMPTY_DRAFT();
                if (!updatedRecord.updateName) {
                    updatedRecord.updateName = updatedRecord.updateBy || "—";
                }
                ElMessage.success("回访记录修改成功");
            } catch (error) {
                ElMessage.error(error.message || "回访记录保存失败，请重试");
            } finally {
                this.saving = false;
            }
        },

        resetEditor() {
            this.isAdding = false;
            this.newDraft = EMPTY_DRAFT();
            this.editingFollowupId = null;
            this.editDraft = EMPTY_DRAFT();
            this.editInitial = EMPTY_DRAFT();
        },

        closeDrawer() {
            if (this.saving) {
                return;
            }
            this.loadRequestId++;
            this.resetEditor();
            this.$emit("update:modelValue", false);
        },

        async requestClose() {
            if (this.saving) {
                ElMessage.warning("回访记录正在保存，请稍候");
                return;
            }
            if (await this.confirmDiscardIfNeeded()) {
                this.closeDrawer();
            }
        },

        handleDrawerBeforeClose(done) {
            if (this.saving) {
                ElMessage.warning("回访记录正在保存，请稍候");
                return;
            }
            const finishClose = () => {
                if (this.saving) {
                    return;
                }
                this.loadRequestId++;
                this.resetEditor();
                this.$emit("update:modelValue", false);
                done();
            };
            if (!this.hasUnsavedChanges()) {
                finishClose();
                return;
            }
            ElMessageBox.confirm("当前填写内容尚未保存，确定关闭回访记录吗？", "提示", {
                confirmButtonText: "放弃并关闭",
                cancelButtonText: "继续填写",
                type: "warning"
            }).then(finishClose).catch(() => {});
        }
    }
};
